// CONTRACT:C2-RENDER-ENGINE.1.0
/** SHARED RENDER-SETTINGS KEY CONTRACT — the single source of the advanced render
 *  parameter names + fallback constants jointly owned by Bundle A (Render) and
 *  Bundle B (Settings & Theming).
 *
 *  CONTRACT (the seam): Bundle B implements the settings STORE + the advanced-tab UI
 *  for EXACTLY the keys named here; Bundle A READS them through {@link resolveRenderSettings}
 *  with the fallback constants below, so the render path is NEVER blocked on B. Both
 *  bundles import these EXACT names from this one module so the key shape can never drift.
 *
 *  IMPORTANT (the I13 / visibility law): every value here is a DISPLAY knob — opacity,
 *  size, vector length, ellipse extent, zoom/altitude gates. NONE of them changes WHAT
 *  renders. A fade/declutter may make a permitted track fainter or smaller; it must NEVER
 *  add a track the PDP denied, nor remove one the PDP permitted. The visibility projection
 *  (host/visibleProjection.ts) decides presence; these settings only decide presentation.
 *
 *  Shape (verbatim contract with Bundle B):
 *    Settings.fade = { neutral, unknown }                       // marker fill opacity [0..1]
 *    Settings.advanced = {
 *      ellipse: { shipSigma, airSigma, unknownAirSigma },       // along-track elongation factors
 *      vectorScale,                                             // velocity-vector + tail multiplier
 *      dropLine: { minZoom, minAltitudeM, groundDotOpacity, lineOpacity },
 *    }
 */

/** Per-affiliation marker fill opacity (clutter reduction). neutral defaults FAINT (0.20),
 *  unknown defaults SEMI (0.70); friend/hostile/pending are never faded (opacity 1). */
export interface FadeSettings {
  /** Opacity [0..1] applied to NEUTRAL-affiliation marker fills. Default 0.20. */
  neutral: number;
  /** Opacity [0..1] applied to UNKNOWN-affiliation marker fills. Default 0.70. */
  unknown: number;
}

/** Per-domain along-track elongation factors for the velocity-correlated error ellipse.
 *  These are the "domainStretch" magnitudes: how far the uncertainty smear stretches ALONG
 *  the heading at full speed (where you'll-be is more uncertain than where-you-are laterally).
 *  Tight ships (small) vs wide unknown/hostile air (large). Cross-track sigma is unchanged;
 *  this only governs the along-track axis. See packages/render-engine/src/ellipse.ts. */
export interface EllipseSettings {
  /** Maritime tracks — tight along-track stretch. Default 0.40. */
  shipSigma: number;
  /** Air/UAS tracks (identified) — moderate along-track stretch. Default 1.00. */
  airSigma: number;
  /** Unknown / hostile air — wide along-track stretch (high directional uncertainty). Default 1.80. */
  unknownAirSigma: number;
}

/** Air/UAS vertical drop-line (stalk + ground dot) gating + dimness. The stalk is
 *  zoom/altitude-gated (declutter — NOT always-on) and the ground dot is small + dim. */
export interface DropLineSettings {
  /** Min MapLibre zoom at/above which un-emphasized air drop-lines draw. Default 7. */
  minZoom: number;
  /** Min altitude (metres HAE) at/above which an air track gets a drop-line. Default 1500. */
  minAltitudeM: number;
  /** Ground-dot fill opacity [0..1]. Default 0.30. */
  groundDotOpacity: number;
  /** Drop-line stroke opacity [0..1]. Default 0.30. */
  lineOpacity: number;
}

/** The advanced-tab render parameters (Bundle B owns the UI; Bundle A owns the names). */
export interface AdvancedRenderSettings {
  ellipse: EllipseSettings;
  /** Multiplier on the velocity-vector leader length + comet-tail length. Default 1.00. */
  vectorScale: number;
  dropLine: DropLineSettings;
}

/** The full render-settings object Bundle A reads. Bundle B persists a superset; A reads
 *  ONLY these keys (everything else in B's store is ignored by the render path). */
export interface RenderSettings {
  fade: FadeSettings;
  advanced: AdvancedRenderSettings;
}

/** A deeply-optional view of {@link RenderSettings} — what B may hand A before every key
 *  is populated. {@link resolveRenderSettings} fills the gaps from the defaults so A is
 *  never blocked on a missing key. */
export interface RenderSettingsInput {
  fade?: Partial<FadeSettings>;
  advanced?: {
    ellipse?: Partial<EllipseSettings>;
    vectorScale?: number;
    dropLine?: Partial<DropLineSettings>;
  };
}

/** THE fallback constants. These are the values Bundle A uses when B's store is absent or a
 *  key is missing. They double as B's documented defaults (so the advanced-tab sliders seed
 *  from the same numbers). Frozen so a consumer can never mutate the shared default. */
export const DEFAULT_RENDER_SETTINGS: Readonly<RenderSettings> = Object.freeze({
  fade: { neutral: 0.2, unknown: 0.7 },
  advanced: {
    ellipse: { shipSigma: 0.4, airSigma: 1.0, unknownAirSigma: 1.8 },
    // Heading-leader + comet-trail length multiplier. 0.25 keeps a short, readable motion cue
    // without the overlapping-spaghetti clutter longer leaders produce at fleet density (operator-tuned).
    vectorScale: 0.25,
    dropLine: { minZoom: 7, minAltitudeM: 1500, groundDotOpacity: 0.3, lineOpacity: 0.3 },
  },
});

/** Clamp helper — fail-safe so a malformed/out-of-range setting never breaks the render
 *  pass (defensive: a bad opacity must not blank a layer). */
function clamp(x: unknown, lo: number, hi: number, fallback: number): number {
  return typeof x === "number" && Number.isFinite(x) ? Math.max(lo, Math.min(hi, x)) : fallback;
}
function num(x: unknown, fallback: number, lo = -Infinity, hi = Infinity): number {
  return typeof x === "number" && Number.isFinite(x) ? Math.max(lo, Math.min(hi, x)) : fallback;
}

/** Resolve a (possibly-partial, possibly-undefined) settings input into a complete, range-safe
 *  {@link RenderSettings}, filling every gap from {@link DEFAULT_RENDER_SETTINGS}. PURE — Bundle A
 *  calls this on every read so it can consume B's store with zero coupling and zero blocking.
 *  Out-of-range / non-numeric values fall back to the default (never throw, never NaN). */
export function resolveRenderSettings(input?: RenderSettingsInput): RenderSettings {
  const D = DEFAULT_RENDER_SETTINGS;
  const f = input?.fade ?? {};
  const a = input?.advanced ?? {};
  const e = a.ellipse ?? {};
  const dl = a.dropLine ?? {};
  return {
    fade: {
      neutral: clamp(f.neutral, 0, 1, D.fade.neutral),
      unknown: clamp(f.unknown, 0, 1, D.fade.unknown),
    },
    advanced: {
      ellipse: {
        shipSigma: num(e.shipSigma, D.advanced.ellipse.shipSigma, 0, 8),
        airSigma: num(e.airSigma, D.advanced.ellipse.airSigma, 0, 8),
        unknownAirSigma: num(e.unknownAirSigma, D.advanced.ellipse.unknownAirSigma, 0, 8),
      },
      vectorScale: num(a.vectorScale, D.advanced.vectorScale, 0, 8),
      dropLine: {
        minZoom: num(dl.minZoom, D.advanced.dropLine.minZoom, 0, 24),
        minAltitudeM: num(dl.minAltitudeM, D.advanced.dropLine.minAltitudeM, 0, 100_000),
        groundDotOpacity: clamp(dl.groundDotOpacity, 0, 1, D.advanced.dropLine.groundDotOpacity),
        lineOpacity: clamp(dl.lineOpacity, 0, 1, D.advanced.dropLine.lineOpacity),
      },
    },
  };
}

/** The dotted key PATHS — string constants Bundle B can address its store with so a typo in
 *  either bundle is a compile/grep miss, not a silent drift. (B may use these or its own typed
 *  accessors; the load-bearing contract is the SHAPE above + these names.) */
export const RENDER_SETTINGS_KEYS = Object.freeze({
  fade: { neutral: "fade.neutral", unknown: "fade.unknown" },
  advanced: {
    ellipse: {
      shipSigma: "advanced.ellipse.shipSigma",
      airSigma: "advanced.ellipse.airSigma",
      unknownAirSigma: "advanced.ellipse.unknownAirSigma",
    },
    vectorScale: "advanced.vectorScale",
    dropLine: {
      minZoom: "advanced.dropLine.minZoom",
      minAltitudeM: "advanced.dropLine.minAltitudeM",
      groundDotOpacity: "advanced.dropLine.groundDotOpacity",
      lineOpacity: "advanced.dropLine.lineOpacity",
    },
  },
} as const);
